import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * 2026-10-28: Vælg et sted (lokation/plads) ved at skrive - i stedet for en lang rullemenu, der ikke
 * kunne søges i. Søgningen dækker navn, kunde, kode, by og adresse; alle ord skal passe (rækkefølgen er
 * ligegyldig, fx "argo køge"). Grupperne (egne lokationer / kunders pladser) står stadig som overskrifter.
 * Kun steder fra listen kan vælges (ingen fri tekst). Tastatur: pil op/ned, Enter, Esc.
 *
 * groups: [{ label, items: [{ value, title, hint, search }] }]
 */
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function PlacePicker({ value, onChange, groups = [], placeholder = "Søg og vælg…", className = "", testid, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const wrap = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const all = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const sel = all.find((it) => it.value === value);
  const rows = useMemo(() => {
    const tokens = norm(q).split(/\s+/).filter(Boolean);
    const out = [];
    for (const g of groups) {
      const hit = g.items.filter((it) => {
        const hay = norm(`${it.title} ${it.hint || ""} ${it.search || ""}`);
        return tokens.every((t) => hay.includes(t));
      });
      if (hit.length) {
        if (groups.length > 1) out.push({ header: g.label });
        hit.forEach((it) => out.push({ item: it }));
      }
    }
    return out;
  }, [groups, q]);
  const items = useMemo(() => rows.filter((r) => r.item).map((r) => r.item), [rows]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 8, above = r.top - 8;
      const up = below < 220 && above > below;
      setPos({ left: r.left, width: Math.max(r.width, 280), maxHeight: Math.max(140, Math.min(340, up ? above : below)),
        ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrap.current && !wrap.current.contains(e.target) && !(listRef.current && listRef.current.contains(e.target))) { setOpen(false); setQ(""); }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); };
  }, [open]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  const pick = (it) => { setOpen(false); setQ(""); inputRef.current?.blur(); if (it.value !== value) onChange(it.value); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && open && items[hi]) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); setQ(""); }
    else if (e.key === "Tab") { setOpen(false); setQ(""); }
  };
  const shown = sel ? `${sel.title}${sel.hint ? ` · ${sel.hint}` : ""}` : "";
  let idx = -1;
  return (
    <div ref={wrap} className="relative">
      {open && <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />}
      <input ref={inputRef} className={`${className} ${open ? "pl-8" : ""} pr-9`} value={open ? q : shown} disabled={disabled} autoComplete="off"
        placeholder={open ? (sel ? `${sel.title} - skriv for at søge (navn, kunde, kode, by)` : "Skriv navn, kunde, kode eller by") : placeholder}
        role="combobox" aria-expanded={open} aria-autocomplete="list" data-testid={testid} title={shown}
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onKeyDown={onKey} />
      {!disabled && (
        <button type="button" tabIndex={-1} aria-label={open ? "Luk" : "Vis listen"} onMouseDown={(e) => e.preventDefault()}
          onClick={() => { if (open && q) { setQ(""); setHi(0); } else if (open) { setOpen(false); } else inputRef.current?.focus(); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700">
          {open && q ? <X className="w-4 h-4" /> : <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />}
        </button>
      )}
      {open && pos && createPortal(
        <ul ref={listRef} role="listbox" data-testid={testid ? `${testid}-options` : undefined}
          style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
          className="z-[1200] overflow-auto rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1">
          {rows.map((r) => {
            if (r.header) {
              return <li key={`h:${r.header}`} role="presentation" className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">{r.header}</li>;
            }
            idx += 1;
            const i = idx, it = r.item;
            return (
              <li key={it.value} role="option" aria-selected={i === hi} data-idx={i}
                onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)} onMouseEnter={() => setHi(i)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-baseline justify-between gap-3 ${i === hi ? "bg-emerald-50 dark:bg-emerald-900/30" : ""} ${it.value === value ? "font-semibold text-emerald-800 dark:text-emerald-300" : "text-slate-700 dark:text-slate-200"}`}>
                <span className="truncate">{it.title}</span>
                {it.hint && <span className="text-[11px] text-slate-400 shrink-0 truncate max-w-[55%]">{it.hint}</span>}
              </li>
            );
          })}
          {!items.length && <li className="px-3 py-2 text-xs text-slate-400">Intet sted matcher "{q}"</li>}
          {q && items.length > 0 && <li role="presentation" className="px-3 pt-1 pb-1 text-[10px] text-slate-400">{items.length} af {all.length}</li>}
        </ul>, document.body
      )}
    </div>
  );
}

/** Pladslisten (/plads) som grupper til PlacePicker: [{ label, items: [plads, ...] }]. */
export function placeGroups(groups) {
  const item = (p) => ({
    value: p.name,
    title: p.name,
    // En egen lokation er jeres egen - ingen kunde i hintet.
    hint: [p.own_location ? "" : p.customer_name, p.location_code, p.city].filter(Boolean).join(" · "),
    search: [p.address, p.code].filter(Boolean).join(" "),
  });
  return groups.filter((g) => g.items.length).map((g) => ({ label: g.label, items: g.items.map(item) }));
}
